// Libs
import { useState } from 'react';
import { useDispatch } from 'react-redux';
// Utils
import GENERAL from 'utils/constants/general';
import { getCoords } from 'utils/libs';
import { getUIValueProp } from 'components/DataFieldManager/OnlyReadField/OnlyReadField';
// Actions
import { sendToast } from 'screens/Dashboard/actions';
import { updateDatasourceItem } from 'core/actions';
// Hooks
import { useOrderApi, useEventApi } from 'hooks';
import { GlobalUtils } from 'utils';
import {
	buildEventAssignmentOrders,
	getEventAssignmentValidationMessage,
} from './useOrderAssignments.utils';

const { UNSUCCESS } = GENERAL.ENV.STATUS;

const normalizeRows = rowData =>
	Array.isArray(rowData) ? rowData : rowData ? [rowData] : [];

const useOrderEventAssignment = () => {
	const dispatch = useDispatch();
	const { getEvents } = useEventApi();
	const { setEventAssignmentV0 } = useOrderApi();

	const [state, setState] = useState({
		isLoading: false,
		isSetting: false,
		data: [],
		status: undefined,
		message: undefined,
		selectedEventId: undefined,
	});

	const onRetry = () =>
		setState(prev => ({ ...prev, status: undefined, message: undefined }));

	const onGetEventAssignment = ({ searchValue } = {}) => {
		setState(prev => ({ ...prev, isLoading: true }));
		getEvents({ searchValue })
			.then(data => {
				setState(prev => ({
					...prev,
					isLoading: false,
					data: GlobalUtils.checkArray(data),
				}));
			})
			.catch(err => {
				setState(prev => ({ ...prev, isLoading: false }));
				dispatch(sendToast({ message: err.message, type: err.type }));
			});
	};

	const onSetEventAssignment = async (
		{ field, rowData, option, ...rest },
		callback,
	) => {
		const rows = normalizeRows(rowData);
		if (!_checkEventAssignment({ option, rows, ...rest })) return false;
		setState(prev => ({ ...prev, isSetting: true }));
		return getCoords()
			.then(({ coords }) => {
				return setEventAssignmentV0(buildEventAssignmentOrders(rows), {
					...rest,
					eventId: option.id,
					coords,
				})
					.then(processedOrders => {
						const processed = GlobalUtils.checkArray(processedOrders);
						const successfulOrders = processed.filter(
							order => order.status !== UNSUCCESS,
						);
						const failedOrders = processed.filter(
							order => order.status === UNSUCCESS,
						);
						if (failedOrders.length) {
							setState(prev => ({
								...prev,
								isSetting: false,
								status: UNSUCCESS,
								message: failedOrders[0].message,
							}));
							return false;
						}

						dispatch(
							updateDatasourceItem({
								updateRecords: successfulOrders.map(order => ({
									id: order.id,
									fields: {
										[field.id]: option.id,
										[getUIValueProp(field) || field.name]: option.name,
									},
								})),
							}),
						);

						setState(prev => ({
							...prev,
							isSetting: false,
							selectedEventId: option.id,
						}));

						if (callback) callback();
						return true;
					})
					.catch(err => {
						setState(prev => ({
							...prev,
							isSetting: false,
							status: UNSUCCESS,
							message: err.message || '',
						}));
						dispatch(sendToast({ message: err.message, type: err.type }));
						return false;
					});
			})
			.catch(({ message, type }) => {
				setState(prev => ({ ...prev, isSetting: false }));
				dispatch(sendToast({ message, type }));
				return false;
			});
	};

	const _checkEventAssignment = ({
		option,
		rows,
		reprogramDate,
		reprogramTime,
	}) => {
		const message = getEventAssignmentValidationMessage({
			option,
			rows,
			reprogramDate,
			reprogramTime,
		});
		if (message) {
			dispatch(
				sendToast({
					type: 'warn',
					message,
				}),
			);
			return false;
		}
		return true;
	};

	return { state, onGetEventAssignment, onSetEventAssignment, onRetry };
};

export default useOrderEventAssignment;
